import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronRight,
  FaLock,
  FaMinus,
  FaPlus,
  FaRegCreditCard,
  FaShoppingBag,
  FaTimes,
  FaTruck,
} from "react-icons/fa";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";

const money = (amount) => `$${amount.toFixed(2)}`;
const priceOf = (product) => Number.parseFloat(String(product.price).replace(/,/g, "")) || 0;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useContext(CartContext);
  const [coupon, setCoupon] = useState("");
  const [couponStatus, setCouponStatus] = useState("");
  const [discountRate, setDiscountRate] = useState(0);

  const lines = useMemo(() => cartItems.map((item) => ({
    item,
    product: productData.find((product) => product.id === item.id),
  })).filter((line) => line.product), [cartItems]);

  const subtotal = lines.reduce((total, { item, product }) => total + priceOf(product) * item.quantity, 0);
  const discount = subtotal * discountRate;
  const shipping = subtotal >= 100 ? 0 : 9.9;
  const total = Math.max(0, subtotal - discount + shipping);
  const shippingProgress = Math.min(100, (subtotal / 100) * 100);

  const applyCoupon = (event) => {
    event.preventDefault();
    if (coupon.trim().toUpperCase() === "TECH10") {
      setDiscountRate(0.1);
      setCouponStatus("Coupon applied — 10% off your order.");
    } else {
      setDiscountRate(0);
      setCouponStatus("That coupon code is not valid. Try TECH10.");
    }
  };

  const goToCheckout = () => {
    navigate("/checkout", { state: { coupon: discountRate ? "TECH10" : "", discountRate } });
  };

  return (
    <main className="cart-page">
      <section className="cart-progress">
        <div className="cart-container">
          <div className="is-active"><span>1</span><strong>Shopping cart</strong></div>
          <i />
          <div><span>2</span><strong>Checkout</strong></div>
          <i />
          <div><span>3</span><strong>Order complete</strong></div>
        </div>
      </section>

      {lines.length === 0 ? (
        <section className="cart-empty cart-container">
          <div className="cart-empty__icon"><FaShoppingBag /><span>0</span></div>
          <h1>Your cart is currently empty.</h1>
          <p>Before proceeding to checkout, add some products to your shopping cart. You will find plenty of useful electronics in our shop.</p>
          <Link to="/shop">RETURN TO SHOP</Link>
        </section>
      ) : (
        <section className="cart-container cart-content">
          <div className="cart-title">
            <div><span>YOUR SELECTION</span><h1>Shopping cart</h1></div>
            <Link to="/shop">Continue shopping <FaChevronRight /></Link>
          </div>

          <div className="cart-layout">
            <div className="cart-products">
              <div className="cart-table-head"><span>PRODUCT</span><span>PRICE</span><span>QUANTITY</span><span>SUBTOTAL</span></div>
              {lines.map(({ item, product }) => (
                <article className="cart-line" key={`${item.id}-${item.size}-${item.color}`}>
                  <div className="cart-line__product">
                    <button type="button" onClick={() => removeFromCart(item.id, item.size, item.color)} aria-label={`Remove ${product.name}`}><FaTimes /></button>
                    <Link to={`/product/${product.id}`} className="cart-line__image"><img src={product.image} alt={product.name} /></Link>
                    <div>
                      <Link to={`/product/${product.id}`}>{product.name}</Link>
                      <span>Color: {item.color || product.colors?.[0] || "Standard"}</span>
                      {item.size && item.size !== "One size" && <span>Size: {item.size}</span>}
                      <small>SKU: DZ-{String(product.id).padStart(4, "0")}</small>
                    </div>
                  </div>
                  <strong className="cart-line__price" data-label="Price">{money(priceOf(product))}</strong>
                  <div className="cart-line__quantity" data-label="Quantity">
                    <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, Math.max(1, item.quantity - 1))} aria-label="Decrease quantity"><FaMinus /></button>
                    <span>{item.quantity}</span>
                    <button type="button" onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)} aria-label="Increase quantity"><FaPlus /></button>
                  </div>
                  <strong className="cart-line__subtotal" data-label="Subtotal">{money(priceOf(product) * item.quantity)}</strong>
                </article>
              ))}

              <div className="cart-products__footer">
                <form onSubmit={applyCoupon}>
                  <input value={coupon} onChange={(event) => setCoupon(event.target.value)} placeholder="Coupon code" aria-label="Coupon code" />
                  <button type="submit">APPLY COUPON</button>
                </form>
                <button type="button" onClick={clearCart}>CLEAR CART</button>
              </div>
              {couponStatus && <p className={discountRate ? "cart-coupon-status is-valid" : "cart-coupon-status"}>{couponStatus}</p>}
            </div>

            <aside className="cart-summary">
              <h2>Cart totals</h2>
              <div className="cart-summary__row"><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
              {discount > 0 && <div className="cart-summary__row cart-summary__discount"><span>Discount (TECH10)</span><strong>−{money(discount)}</strong></div>}
              <div className="cart-summary__shipping">
                <div><span>Shipping</span><strong>{shipping === 0 ? "Free shipping" : money(shipping)}</strong></div>
                <p>{shipping === 0 ? "Your order qualifies for free shipping." : "Flat-rate tracked delivery. Free shipping from $100."}</p>
                <Link to="/shipping">View shipping information</Link>
              </div>
              <div className="cart-summary__row cart-summary__total"><span>Total</span><strong>{money(total)}</strong></div>
              <button type="button" className="cart-summary__checkout" onClick={goToCheckout}>PROCEED TO CHECKOUT <FaChevronRight /></button>
              <p className="cart-summary__secure"><FaLock /> Secure, encrypted checkout</p>
            </aside>
          </div>

          <div className="cart-shipping-progress">
            <div><FaTruck /><span>{shipping === 0 ? "You’ve unlocked free shipping!" : `Add ${money(100 - subtotal)} more to get free shipping.`}</span></div>
            <div><i style={{ width: `${shippingProgress}%` }} /></div>
          </div>

          <div className="cart-assurances">
            <div><FaTruck /><span><strong>Fast tracked delivery</strong>Order tracking by email</span></div>
            <div><FaRegCreditCard /><span><strong>Flexible payment</strong>Major cards accepted</span></div>
            <div><FaLock /><span><strong>Secure checkout</strong>Your details stay protected</span></div>
          </div>
        </section>
      )}
    </main>
  );
};

export default CartPage;
